<script lang="ts">
import type { PrimitiveProps } from 'reka-ui'

export interface NavRailItemProps extends PrimitiveProps {
  label: string
  expanded: boolean | 'responsive'
  active?: boolean
  open?: boolean
  hasSlideover?: boolean
}
</script>

<script setup lang="ts">
import { Primitive } from 'reka-ui'
import { computed } from 'vue'
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '#ui-kit/ui/tooltip'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<NavRailItemProps>(), {
  as: 'button',
  active: false,
  open: false,
  hasSlideover: false,
})

const emit = defineEmits<{
  open: [event: Event]
  close: [event: Event]
}>()

const resolvedType = computed(() => (props.as === 'button' && !props.asChild ? 'button' : undefined))
const collapsedClass =
  'relative flex size-10 items-center justify-center rounded-lg text-primal-inverted transition-colors hover:bg-accent'
const activeDotClass = 'size-2 rounded-full bg-accent-inverted'

const hasTooltip = computed(() => props.expanded !== true && !props.hasSlideover)

const itemClass = computed(() => {
  if (props.expanded === 'responsive') {
    return `${collapsedClass} text-xs font-normal leading-none nav-expanded:h-auto nav-expanded:w-full nav-expanded:justify-start nav-expanded:gap-4 nav-expanded:px-1.5 nav-expanded:py-2`
  }
  if (props.expanded) {
    return 'flex w-full items-center gap-4 rounded-lg px-1.5 py-2 text-xs font-normal leading-none text-primal-inverted transition-colors hover:bg-accent'
  }
  return collapsedClass
})

const labelClass = computed(() =>
  props.expanded === 'responsive' ? 'hidden truncate nav-expanded:inline' : 'truncate',
)

const activeDotPositionClass = computed(() => {
  if (props.expanded === 'responsive') {
    return 'absolute right-1 top-1 nav-expanded:static nav-expanded:ml-auto nav-expanded:shrink-0'
  }
  return props.expanded ? 'ml-auto shrink-0' : 'absolute right-1 top-1'
})

function onEnter(event: Event) {
  if (props.hasSlideover) {
    emit('open', event)
  }
}

function onLeave(event: Event) {
  if (!props.hasSlideover) {
    return
  }
  const target = event.currentTarget
  if (!(target instanceof HTMLElement)) {
    return
  }
  if (target.matches(':hover') || target.contains(document.activeElement)) {
    return
  }
  emit('close', event)
}
</script>

<template>
  <Tooltip v-if="hasTooltip">
    <TooltipTrigger as-child>
      <Primitive
        :as="as"
        :as-child="asChild"
        :type="resolvedType"
        v-bind="$attrs"
        :aria-label="label"
        :aria-current="active ? 'page' : undefined"
        :class="itemClass"
      >
        <slot name="icon" />
        <span v-if="expanded" :class="labelClass">{{ label }}</span>
        <span v-if="active" :class="[activeDotClass, activeDotPositionClass]" />
      </Primitive>
    </TooltipTrigger>
    <TooltipContent
      side="right"
      :side-offset="12"
      class="bg-brand-900 px-3 py-2 text-xs font-semibold text-primal-inverted"
      :class="{ 'nav-expanded:hidden': expanded === 'responsive' }"
    >
      {{ label }}
      <TooltipArrow class="fill-brand-900" />
    </TooltipContent>
  </Tooltip>

  <Primitive
    v-else
    :as="as"
    :as-child="asChild"
    :type="resolvedType"
    v-bind="$attrs"
    :aria-label="expanded === true ? undefined : label"
    :aria-current="active ? 'page' : undefined"
    :aria-expanded="hasSlideover ? open : undefined"
    :class="[itemClass, { 'bg-accent': open }]"
    @mouseenter="onEnter"
    @focusin="onEnter"
    @mouseleave="onLeave"
    @focusout="onLeave"
  >
    <slot name="icon" />
    <span v-if="expanded" :class="labelClass">{{ label }}</span>
    <span v-if="active" :class="[activeDotClass, activeDotPositionClass]" />
  </Primitive>
</template>
